import { useTranslation } from "react-i18next";
import { Button } from "@carbon/react";
import "./bid.scss";

export type SellerOfferActionsProps = {
  isInverseOpen: boolean;
  isEditMode: boolean;
  canEditNormalOffer: boolean;
  canShowDeclineToggle: boolean;
  isDeclined: boolean;
  isUpdating: boolean;
  isUpdatingDetails: boolean;
  isDeclining: boolean;
  updateDisabled: boolean;
  saveDisabled: boolean;
  onOpenDecline: () => void;
  onInverseUpdate: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
};

/**
 * The seller's action bar under the offer line (Task 6) — the "split into … actions" piece.
 * Renders exactly one of three mutually-exclusive states, mirroring the pre-Carbon container:
 * an OPEN inverse order (decline + submit-bid), an OPEN normal order not being edited (decline
 * only), or an OPEN normal order in edit mode (cancel + save). Every gating boolean is computed
 * by `SellerOffer.tsx` and passed in, so the state machine isn't duplicated here.
 */
const SellerOfferActions = ({
  isInverseOpen,
  isEditMode,
  canEditNormalOffer,
  isUpdating,
  isUpdatingDetails,
  updateDisabled,
  saveDisabled,
  onInverseUpdate,
  onCancelEdit,
  onSaveEdit,
}: SellerOfferActionsProps) => {
  const { t } = useTranslation();

  // The "Will not quote" (decline whole order) action now lives in the header beside Chat — see
  // SellerOfferHeader. This bar only carries the price-editing actions.
  if (isInverseOpen) {
    return (
      <div className="seller-offer__actions">
        <Button size="sm" disabled={updateDisabled} onClick={onInverseUpdate}>
          {isUpdating ? t("cta.loading") : t("cta.update")}
        </Button>
      </div>
    );
  }

  if (isEditMode && canEditNormalOffer) {
    return (
      <div className="seller-offer__actions">
        <Button kind="secondary" size="sm" onClick={onCancelEdit}>
          {t("cta.cancel")}
        </Button>
        <Button size="sm" disabled={saveDisabled} onClick={onSaveEdit}>
          {isUpdatingDetails ? t("cta.loading") : t("cta.edit")}
        </Button>
      </div>
    );
  }

  return null;
};

export default SellerOfferActions;
